/**
 * Unified System Status Page
 *
 * Dashboard with time mode toggle (Today/Yesterday/Custom).
 * System Resources section always shown at top with real-time data.
 * Historical modes fetch daily report data for selected date.
 */

import React from 'react'
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { format, parseISO } from 'date-fns'

import TimeToggle from './TimeToggle'
import AdminPageHeader from '../../../../components/admin/shared/AdminPageHeader'
import useSystemStatus from './useSystemStatus'
import {
  AttentionRequired,
  SyncSummary,
  HealthOverview,
  SystemConnectivity,
  ActivityHighlights,
  UserLogins,
  MaintenanceTasks,
  SystemResources
} from './sections'

const SystemStatus: React.FC = () => {
  const {
    mode,
    setMode,
    customDate,
    setCustomDate,
    data,
    isLoading,
    error,
    refetch,
  } = useSystemStatus()

  return (
    <>
      <AdminPageHeader
        pageKey="systemStatus"
        contextChips={
          data?.report_date
            ? [{ label: format(parseISO(data.report_date), 'EEEE, MMMM d, yyyy') }]
            : undefined
        }
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimeToggle
              mode={mode}
              customDate={customDate}
              onModeChange={setMode}
              onDateChange={setCustomDate}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || 'Failed to load system status'}
        </Alert>
      )}

      {/* Content */}
      {data && !isLoading && (
        <>
          {/* Attention Required - Always at top */}
          <AttentionRequired items={data.attention_required} />

          {/* System Resources - Collapsible with real-time data */}
          <Box sx={{ mb: 3 }}>
            <SystemResources />
          </Box>

          {/* Main Grid */}
          <Grid container spacing={3}>
            {/* Row 1: Sync Summary + Health Overview */}
            <Grid item xs={12} md={6}>
              <SyncSummary data={data.sync_summary} />
            </Grid>
            <Grid item xs={12} md={6}>
              <HealthOverview
                data={data.health_overview}
                newExceptionsCount={data.new_exceptions_count}
              />
            </Grid>

            {/* Row 2: Connectivity + Activity */}
            <Grid item xs={12} md={6}>
              <SystemConnectivity data={data.connectivity} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ActivityHighlights data={data.activity_highlights} />
            </Grid>

            {/* Row 3: User Logins + Maintenance */}
            <Grid item xs={12} md={6}>
              <UserLogins data={data.user_logins} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MaintenanceTasks data={data.maintenance} />
            </Grid>
          </Grid>

          {/* Generated timestamp */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Report generated at {format(parseISO(data.generated_at), 'PPpp')}
            </Typography>
          </Box>
        </>
      )}
    </>
  )
}

export default SystemStatus
